import {
  bulkChanges,
  grantedLookup,
  groupPermissions,
  type PermissionChange,
  useAdminPermissions,
  useBulkUpdateRolePermissions,
  useUpdateRolePermission,
} from '../../../features/admin/use-admin-permissions'
import { AdminPage, adminError } from './admin-bits'
import { EmptyRow, Inline } from './controls'
import { Card, mono } from './ui'

const linkBtn =
  'text-[11px] text-[var(--color-accent)] hover:underline disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-[var(--color-accent)]'

export function PorcelainAdminPermissions() {
  const q = useAdminPermissions()
  const one = useUpdateRolePermission()
  const bulk = useBulkUpdateRolePermissions()
  const roles = q.data?.roles ?? []
  const groups = groupPermissions(q.data?.permissions ?? [])
  const granted = grantedLookup(roles)
  const err = one.error ?? bulk.error
  const apply = (changes: PermissionChange[]) => {
    if (changes.length) bulk.mutate({ changes })
  }
  return (
    <AdminPage
      title="Permissions"
      subtitle="Which roles can do what. Changes can take up to five minutes to apply."
      loading={q.isLoading}
      error={!!q.error}
    >
      {err ? <Inline tone="bad">{adminError(err)} The change was reverted.</Inline> : null}
      {groups.length === 0 ? (
        <Card>
          <EmptyRow>No permissions defined.</EmptyRow>
        </Card>
      ) : null}
      {groups.map((g) => {
        const ids = g.permissions.map((p) => p.id)
        return (
          <Card key={g.groupTag} title={g.groupTag}>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[14px]">
                <thead>
                  <tr>
                    <th className="h-12 px-5 text-left text-[12px] font-medium uppercase tracking-[0.04em] text-[var(--color-muted)]">
                      Permission
                    </th>
                    {roles.map((r) => (
                      <th key={r.id} className="px-3 text-center text-[12px] font-medium text-[var(--color-muted)]">
                        <div>{r.name}</div>
                        <div className="flex justify-center gap-1">
                          <button
                            type="button"
                            className={linkBtn}
                            disabled={bulk.isPending}
                            onClick={() => apply(bulkChanges(roles, r.id, ids, true))}
                          >
                            all
                          </button>
                          <span aria-hidden="true" className="text-[11px]">
                            /
                          </span>
                          <button
                            type="button"
                            className={linkBtn}
                            disabled={bulk.isPending}
                            onClick={() => apply(bulkChanges(roles, r.id, ids, false))}
                          >
                            none
                          </button>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {g.permissions.map((p) => (
                    <tr key={p.id} className="border-t border-[var(--color-border)] hover:bg-[var(--v1-accent-soft)]">
                      <td className="h-11 px-5 text-[13px]" style={mono}>
                        {p.name}
                      </td>
                      {roles.map((r) => {
                        const on = granted.get(r.id)?.has(p.id) ?? false
                        return (
                          <td key={r.id} className="px-3 text-center">
                            <input
                              type="checkbox"
                              className="size-4 accent-[var(--color-accent)]"
                              aria-label={`${r.name}: ${p.name}`}
                              checked={on}
                              onChange={() =>
                                one.mutate({ roleId: r.id, permissionId: p.id, action: on ? 'revoke' : 'grant' })
                              }
                            />
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )
      })}
    </AdminPage>
  )
}
